import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import type { IconName } from '../components/ui/Icon';

/**
 * Navigazione del telefono.
 *
 * Perché un file a parte e non la costante NAV dentro Sidebar.tsx: la barra in
 * basso e la sidebar mostrano le STESSE sezioni ma in due ordini diversi (in
 * basso stanno solo le quattro voci decise, il resto finisce sotto "Altro"), e
 * soprattutto in questo momento sul repo lavorano più mani. Tenere la lista qui
 * evita di toccare Sidebar.tsx per esportarne una costante.
 *
 * ⚠️ DEBITO NOTO: oggi la stessa lista vive in due posti (qui e in
 * Sidebar.tsx). Quando la sidebar si tocca per un altro motivo, va fatta
 * importare da qui e la copia là va cancellata. Finché sono due, una sezione
 * nuova va aggiunta in ENTRAMBI i file.
 */

export interface NavEntry {
  key: string;
  to: string;
  /** Etichetta lunga, quella del pannello "Altro" e della sidebar */
  label: string;
  /** Etichetta corta sotto l'icona della barra in basso (max ~11 caratteri) */
  short?: string;
  icon: IconName;
  module: ModuleKey;
}

/**
 * Le quattro voci della barra in basso. Decise con Lorenzo: sono le cose che si
 * guardano dal telefono più volte al giorno. L'ordine è quello della giornata:
 * cosa devo fare oggi, cosa ho in sospeso, cosa sto trattando, come stanno i
 * soldi. "Altro" lo aggiunge il componente, non è una sezione.
 */
export const MOBILE_TABS: NavEntry[] = [
  { key: 'oggi',   to: '/',            label: 'Oggi',            short: 'Oggi',   icon: 'dashboard', module: ModuleKey.DASHBOARD },
  { key: 'tasks',  to: '/tasks',       label: 'Task',            short: 'Task',   icon: 'tasks',     module: ModuleKey.TASKS },
  { key: 'deals',  to: '/trattative',  label: 'Trattative',      short: 'Deal',   icon: 'handshake', module: ModuleKey.TRATTATIVE },
  // Porta alla Situazione. Le altre cinque pagine (/admin/fatture, …) tengono
  // accesa la voce perché isNavActive confronta il prefisso; dal telefono ci
  // si arriva dal blocco "Le altre pagine" in fondo alla Situazione.
  { key: 'admin',  to: '/admin',       label: 'Amministrazione', short: 'Ammin.', icon: 'landmark',  module: ModuleKey.AMMINISTRAZIONE },
];

/**
 * Tutto il resto, raggruppato come nella sidebar: chi apre "Altro" ritrova le
 * stesse famiglie con gli stessi nomi, così il telefono non insegna una mappa
 * diversa da quella del computer.
 */
export const MOBILE_MORE: Array<{ title: string; items: NavEntry[] }> = [
  {
    title: 'Operativo',
    items: [
      { key: 'crm',       to: '/crm',       label: 'Contatti',        icon: 'users', module: ModuleKey.CRM },
      { key: 'suppliers', to: '/suppliers', label: 'Professionisti',  icon: 'brief', module: ModuleKey.SUPPLIERS },
    ],
  },
  {
    // Come in Sidebar.tsx: le tre divisioni sono diventate un filtro dentro
    // Progetti. Finché i due elenchi restano separati, una voce nuova va
    // aggiunta in tutti e due (debito noto, scritto in os-planning/mobile.md).
    title: 'Lavoro',
    items: [
      { key: 'progetti', to: '/progetti', label: 'Progetti',        icon: 'cube',   module: ModuleKey.CRM },
      { key: 'talent',   to: '/talent',   label: 'Talent Sourcing', icon: 'talent', module: ModuleKey.TALENT },
    ],
  },
  {
    title: 'Back-office',
    items: [
      { key: 'services', to: '/services', label: 'Abbonamenti',      icon: 'repeat',   module: ModuleKey.SERVICES },
      { key: 'vault',    to: '/vault',    label: 'Password Vault',  icon: 'lock',     module: ModuleKey.VAULT },
      { key: 'report',   to: '/report',   label: 'Report',          icon: 'chart',    module: ModuleKey.REPORT },
      { key: 'settings', to: '/settings', label: 'Impostazioni',    icon: 'settings', module: ModuleKey.SETTINGS },
    ],
  },
];

/** Permesso minimo di lettura su un modulo: stessa regola della Sidebar. */
export function canSeeModule(
  user: { permissions: Array<{ module: ModuleKey; level: PermissionLevel }> } | null | undefined,
  module: ModuleKey,
): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl, PermissionLevel.READ);
}

/**
 * La voce è quella attiva? Esatta per la home ("/"), per prefisso per le altre,
 * così anche /trattative/123 tiene accesa la voce "Trattative".
 */
export function isNavActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(to + '/');
}
